/**
 * Variant colour words → swatch + readable text colours per theme.
 *
 * Shared by the Products cards and the picking line cards so "Vivid Yellow"
 * reads as yellow everywhere staff pick by colour (Rhys 2026-09-18). Lives
 * here rather than in ProductCard so PickingModal does not import a card.
 */

/**
 * Colour words a variant title can carry → swatch + readable text colours
 * per theme (a "White" variant can't render white-on-white; "Black" can't
 * vanish on a dark card — the dot always shows the TRUE colour).
 */
export const VARIANT_COLORS = {
  red: { swatch: '#e11d48', lightText: '#be123c', darkText: '#fb7185' },
  crimson: { swatch: '#dc143c', lightText: '#b91c3c', darkText: '#fb7185' },
  maroon: { swatch: '#7f1d1d', lightText: '#7f1d1d', darkText: '#f87171' },
  orange: { swatch: '#f97316', lightText: '#c2410c', darkText: '#fb923c' },
  amber: { swatch: '#f59e0b', lightText: '#b45309', darkText: '#fbbf24' },
  yellow: { swatch: '#eab308', lightText: '#a16207', darkText: '#facc15' },
  gold: { swatch: '#d4af37', lightText: '#92700c', darkText: '#e5c15c' },
  lime: { swatch: '#84cc16', lightText: '#4d7c0f', darkText: '#a3e635' },
  green: { swatch: '#22c55e', lightText: '#15803d', darkText: '#4ade80' },
  emerald: { swatch: '#10b981', lightText: '#047857', darkText: '#34d399' },
  mint: { swatch: '#6ee7b7', lightText: '#047857', darkText: '#6ee7b7' },
  olive: { swatch: '#6b8e23', lightText: '#556b2f', darkText: '#9acd32' },
  teal: { swatch: '#14b8a6', lightText: '#0f766e', darkText: '#2dd4bf' },
  cyan: { swatch: '#06b6d4', lightText: '#0e7490', darkText: '#22d3ee' },
  turquoise: { swatch: '#40e0d0', lightText: '#0e7490', darkText: '#5eead4' },
  blue: { swatch: '#3b82f6', lightText: '#1d4ed8', darkText: '#60a5fa' },
  navy: { swatch: '#1e3a8a', lightText: '#1e3a8a', darkText: '#93c5fd' },
  indigo: { swatch: '#6366f1', lightText: '#4338ca', darkText: '#818cf8' },
  purple: { swatch: '#a855f7', lightText: '#7e22ce', darkText: '#c084fc' },
  violet: { swatch: '#8b5cf6', lightText: '#6d28d9', darkText: '#a78bfa' },
  magenta: { swatch: '#d946ef', lightText: '#a21caf', darkText: '#e879f9' },
  pink: { swatch: '#ec4899', lightText: '#be185d', darkText: '#f472b6' },
  rose: { swatch: '#f43f5e', lightText: '#be123c', darkText: '#fb7185' },
  coral: { swatch: '#ff7f50', lightText: '#c2410c', darkText: '#fda47a' },
  brown: { swatch: '#92400e', lightText: '#78350f', darkText: '#d6a05c' },
  tan: { swatch: '#d2b48c', lightText: '#8a6d3b', darkText: '#d2b48c' },
  beige: { swatch: '#e8dcc2', lightText: '#8a7a55', darkText: '#e8dcc2' },
  cream: { swatch: '#f5f0dc', lightText: '#8a7a55', darkText: '#f5f0dc' },
  ivory: { swatch: '#fffff0', lightText: '#8a8a6d', darkText: '#fffff0' },
  white: { swatch: '#ffffff', lightText: '#4b5563', darkText: '#f3f4f6' },
  silver: { swatch: '#c0c0c0', lightText: '#6b7280', darkText: '#d1d5db' },
  grey: { swatch: '#6b7280', lightText: '#4b5563', darkText: '#b7bcc4' },
  gray: { swatch: '#6b7280', lightText: '#4b5563', darkText: '#b7bcc4' },
  charcoal: { swatch: '#36454f', lightText: '#36454f', darkText: '#a8b3bb' },
  black: { swatch: '#111111', lightText: '#111111', darkText: '#e5e7eb' },
  clear: { swatch: 'transparent', lightText: '#6b7280', darkText: '#c9cdd3' },
  transparent: { swatch: 'transparent', lightText: '#6b7280', darkText: '#c9cdd3' },
  titanium: { swatch: '#b6b1a9', lightText: '#6b6459', darkText: '#cfc9c0' }
};

/** First recognised colour word in a variant title ("Red", "Blue / 2CW"). */
export function resolveVariantColor(variantTitle) {
  const words = String(variantTitle || '').toLowerCase().match(/[a-z]+/g) || [];
  for (const word of words) {
    if (VARIANT_COLORS[word]) return VARIANT_COLORS[word];
  }
  return null;
}
